/**
 * Each table's last filter and sort, kept for the whole signed-in
 * session and shared by every browser tab of it (`useFetchList` reads
 * and writes it).
 *
 * localStorage, so a list opened in a new tab comes back the way the
 * user left it. The value can hold patient names (text filters), so it
 * is stored masked with the same scheme as the filter history
 * (`../filterHistory/mask`): unreadable in plain DevTools and to another
 * user of the browser. What protects it is its lifetime, as with the
 * history: `clearStoredTableFilters` runs on every sign-out and sign-in
 * (userSlice) and `sweepTableFiltersIfSignedOut` when the dashboard
 * opens without a session.
 *
 * Nothing else ends it - no daily expiry, no reset on left-nav
 * navigation (the client asked for both to go, 2026-11-05). Only the
 * user clearing a filter, or a caller that sets a fresh pre-filter
 * (`clearTableFilters`), removes an entry.
 *
 * Every access is guarded: storage can be full, blocked or disabled,
 * and a remembered filter is a convenience that must never break a table.
 */
import { mask, maskKey, unmask } from '../filterHistory/mask';

export const STORAGE_KEY = 'qhs.tableFilters';

// Scope of tables rendered before an account is known.
const NO_ACCOUNT_SCOPE = 'user';

const isSignedIn = () => {
  try {
    return localStorage.getItem('isLogin') === 'true';
  } catch (error) {
    return false;
  }
};

export const clearStoredTableFilters = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Nothing stored that we could reach.
  }
};

export const sweepTableFiltersIfSignedOut = () => {
  if (!isSignedIn()) clearStoredTableFilters();
};

// { owner, accounts: { [accountScope]: { [tableKey]: { sort, filter } } } }
// for this user; anything else (another user's, an older build's, a
// damaged item) is dropped.
const read = userUuid => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { owner: userUuid, accounts: {} };
    const data = unmask(raw, maskKey(userUuid));
    if (data?.owner === userUuid && data.accounts && typeof data.accounts === 'object') {
      return data;
    }
  } catch (error) {
    // Fall through to an empty store.
  }
  clearStoredTableFilters();
  return { owner: userUuid, accounts: {} };
};

const write = (userUuid, data) => {
  try {
    localStorage.setItem(STORAGE_KEY, mask(data, maskKey(userUuid)));
  } catch (error) {
    // Storage full or blocked: the table simply does not remember.
  }
};

const accountScope = accountUuid => accountUuid || NO_ACCOUNT_SCOPE;

/** `{ sort, filter }` last stored for the table, or `{}`. */
export const readTableState = (userUuid, accountUuid, tableKey) => {
  if (!userUuid || !tableKey) return {};
  return read(userUuid).accounts[accountScope(accountUuid)]?.[tableKey] || {};
};

/**
 * Stores the table's filter and sort. Refused once the session is over:
 * a table unmounting during sign-out must not write the value back
 * after it was cleared.
 */
export const writeTableState = (userUuid, accountUuid, tableKey, sort, filter) => {
  if (!userUuid || !tableKey || !isSignedIn()) return;
  const data = read(userUuid);
  const scope = accountScope(accountUuid);
  data.accounts[scope] = { ...(data.accounts[scope] || {}), [tableKey]: { sort, filter } };
  write(userUuid, data);
};

/**
 * Drops the stored filter and sort of the given tables in one account, so
 * `useFetchList` falls back to the caller's `baseFilter` on its next
 * mount - e.g. the sidebar's Alerts menu pushes a fresh pre-filter that
 * would otherwise be shadowed by whatever the user last picked there.
 */
export const clearTableFilters = (userUuid, accountUuid, tableKeys) => {
  if (!userUuid || !accountUuid || !tableKeys?.length) return;
  const data = read(userUuid);
  const bucket = data.accounts[accountUuid];
  if (!bucket) return;
  tableKeys.forEach(key => {
    delete bucket[key];
  });
  write(userUuid, data);
};
